import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MailPlus, Trash2 } from "lucide-react";
import { api, errMsg, fmtDate, ROLE_LABELS } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { UserRgpdActions } from "@/components/admin/UserRgpdActions";
import { UserAccountActions } from "@/components/admin/UserAccountActions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function AdminUsers() {
  const { me } = useMe();
  const [data, setData] = useState({ roles: [], users: [] });
  const [loaded, setLoaded] = useState(false);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("salarie");
  const load = () => {
    api.get("/users").then((r) => { setData(r.data); setLoaded(true); }).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/invites").then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); load(); } };

  const invite = (e) => {
    e.preventDefault();
    act(async () => {
      const { data: d } = await api.post("/admin/invites", { email, role });
      if (!d.email_sent && d.status === "pending") toast.message("Invitation enregistrée : la personne obtiendra ce rôle en créant son compte avec cet email.");
      setEmail("");
    }, "Invitation créée");
  };

  return (
    <div className="space-y-6">
      <form onSubmit={invite} className="bg-white border rounded-xl p-4 flex flex-col md:flex-row gap-2 md:items-center">
        <div className="flex items-center gap-2 font-semibold text-sm md:w-48"><MailPlus className="h-4 w-4 text-[#1E3A8A]" />Inviter un collaborateur</div>
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom.nom@sdrpneus.fr" className="flex-1" data-testid="invite-email-input" />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="md:w-44" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="submit" data-testid="invite-submit-btn">Inviter</Button>
      </form>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="users-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="text-left px-4 py-3">Utilisateur</th><th className="text-left px-4 py-3">Rôle</th><th className="text-left px-4 py-3">Dernière connexion</th><th className="text-left px-4 py-3">Accès actif</th><th className="text-left px-4 py-3">RGPD</th></tr>
          </thead>
          <tbody className="divide-y">
            {!loaded && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Chargement…</td></tr>}
            {data.users.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <td className="px-4 py-3"><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => act(() => api.patch(`/users/${u.user_id}/role`, { role: v }), "Rôle mis à jour")}>
                    <SelectTrigger className="w-44 h-9" data-testid={`user-role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(u.last_login, true)}<div className="font-mono text-slate-400">{u.last_ip}</div></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!u.active} disabled={u.user_id === me.user_id} data-testid={`user-active-switch-${u.user_id}`}
                      onCheckedChange={(v) => act(() => api.patch(`/users/${u.user_id}/status`, { active: v }).then((r) => { if (r.data.login_synced === false) toast.warning("Le compte est bloqué dans la plateforme, mais la déconnexion immédiate a échoué : réessayez dans un instant."); return r; }), v ? "Compte activé" : "Compte désactivé")} />
                    <span className={`text-xs font-semibold ${u.active ? "text-emerald-700" : "text-red-600"}`}>{u.active ? "Actif" : "Désactivé / en attente"}</span>
                  </div>
                </td>
                <td className="px-4 py-3"><div className="flex items-center gap-1"><UserRgpdActions user={u} users={data.users} isSelf={u.user_id === me.user_id} onDone={load} /><UserAccountActions user={u} isSelf={u.user_id === me.user_id} /></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invites.length > 0 && (
        <div className="bg-white border rounded-xl">
          <div className="px-4 py-3 border-b font-semibold text-sm">Invitations</div>
          <div className="divide-y">
            {invites.map((i) => (
              <div key={i.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`invite-row-${i.id}`}>
                <span className="flex-1 truncate">{i.email}</span>
                <span className="text-xs">{ROLE_LABELS[i.role]}</span>
                <span className={`text-xs font-semibold ${i.status === "accepted" ? "text-emerald-700" : "text-amber-700"}`}>{i.status === "accepted" ? "Acceptée" : "En attente"}</span>
                <span className="text-xs text-slate-500 hidden sm:inline">{fmtDate(i.created_at)}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Supprimer" onClick={() => act(() => api.delete(`/admin/invites/${i.id}`), "Invitation supprimée")}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
